import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { LemonadeOnboarding } from '@/components/auth/LemonadeOnboarding';
import { LemonadeLogin } from '@/components/auth/LemonadeLogin';
import { useAuth } from '@/hooks/useAuth';
import { SEOHead } from '@/components/SEOHead';
import { sanitizeNext } from '@/lib/authNext';
import { captureAttributionFromSearchParams } from '@/lib/analytics/properties';
import reklaimWordmark from '@/assets/reklaim-wordmark.png';
import { RotateCcw, AlertCircle, X } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // A one-tap login link that has expired redirects here (AuthOneTap) with the
  // reason in router state. Surface it as a dismissible notice above the form.
  const authErrorFromState =
    (location.state as { authError?: string } | null)?.authError ?? null;
  const [oneTapNotice, setOneTapNotice] = useState<string | null>(
    authErrorFromState,
  );
  // React Router keeps this component mounted across navigations to /auth, so
  // the initial useState only captures the first router-state notice. Re-sync
  // on every navigation (location.key is unique per navigation) so a second
  // expired one-tap link still surfaces its notice, even when the message text
  // is identical. Manual dismissal within a single navigation still sticks.
  useEffect(() => {
    setOneTapNotice(authErrorFromState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);
  const { isAuthenticated, isLoading, accountLockedError, setAccountLockedError, sessionError, setSessionError } = useAuth();
  const modeFromUrl = searchParams.get('mode');
  // Consumers have no password — only `onboarding` and `login` are supported.
  // Admin auth is Clerk email OTP at /admin/login (REK-2166 PR2 retired password-reset routes).
  const [mode, setMode] = useState<'onboarding' | 'login'>(
    modeFromUrl === 'onboarding' || modeFromUrl === 'signup'
      ? 'onboarding'
      : 'login'
  );
  
  // Track if user is in the middle of signup flow - prevents auto-redirect
  const [isCompletingSignup, setIsCompletingSignup] = useState(false);
  // Email to pre-fill in login when an existing user is redirected from onboarding
  const [loginEmail, setLoginEmail] = useState<string | undefined>(undefined);
  // REK-1903: whether the login OTP was already sent upstream (the signup funnel
  // detected an existing account and sent it), so the login screen should show
  // the "Check your email" stage without re-sending.
  const [loginAlreadySent, setLoginAlreadySent] = useState(false);
  // Email to pre-fill in onboarding when a non-existent user is redirected from login
  const [signupEmail, setSignupEmail] = useState<string | undefined>(undefined);

  // REK-1924: where to send the user after auth. `undefined` when no `next` is
  // present, so the existing flows keep their own defaults unchanged; a survey
  // deep link (or the dashboard guard) sets `?next=` to the requested survey path.
  // Open-redirect-guarded (same-origin only) by sanitizeNext.
  const rawNext = searchParams.get('next');
  const nextPath = rawNext ? sanitizeNext(rawNext) : undefined;
  // Referral still passed explicitly into LemonadeOnboarding; Meta/UTM live in
  // reklaim_attribution via captureAttributionFromSearchParams (REK-2146).
  const refCode = searchParams.get('ref');

  // Get email from URL or localStorage
  const emailFromUrl = searchParams.get('email');
  const initialEmail = emailFromUrl || localStorage.getItem('reklaim_email') || undefined;
  // REK-1903: the signup funnel redirects existing users here as
  // /auth?mode=login&email=...&sent=1 (login OTP already sent). Surface that to
  // the login screen so it lands on the OTP "Check your email" stage directly.
  const sentFromUrl = searchParams.get('sent') === '1';
  // REK-2008 (SSO.14, Track D): under the Clerk engine, a bare /auth?email=…
  // (the homepage-hero handoff from the marketing site or the Vite hero) also
  // feeds the LOGIN box — its auto-send probe then routes existing users
  // straight to the OTP entry and unknown emails into the in-card sign-up
  // Clerk supports both sent=1 and direct ?email= prefill (SSO Wave 3, REK-2166).
  const loginInitialEmail =
    loginEmail ?? (emailFromUrl || undefined);

  // Store UTM + Meta click IDs in reklaim_attribution (REK-2146)
  const search = searchParams.toString();
  useEffect(() => {
    captureAttributionFromSearchParams(new URLSearchParams(search));
  }, [search]);

  // Callback for when signup flow starts password step
  const handleSignupStarted = useCallback(() => {
    setIsCompletingSignup(true);
  }, []);

  // Callback for when signup completes (user finishes or skips profile wizard)
  const handleSignupComplete = useCallback(() => {
    setIsCompletingSignup(false);
    navigate(nextPath ?? '/dashboard');
  }, [navigate, nextPath]);

  // Redirect authenticated users to their destination - BUT NOT during signup
  // flow, if account is locked, or if a fail-closed session-verify error is
  // pending (REK-1754 — otherwise the brief isAuthenticated window before the
  // sign-out propagates would bounce the user to the dashboard and they'd never
  // see the "Please sign in again" banner). Falls back to /dashboard (REK-1924).
  useEffect(() => {
    if (isAuthenticated && !isLoading && !isCompletingSignup && !accountLockedError && !sessionError) {
      navigate(nextPath ?? '/dashboard');
    }
  }, [isAuthenticated, isLoading, isCompletingSignup, accountLockedError, sessionError, navigate, nextPath]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-pulse">
          <img 
            src={reklaimWordmark} 
            alt="Reklaim" 
            className="h-8 opacity-50"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEOHead
        title="Get Started | Reklaim"
        description="Create your free Reklaim account and start earning rewards from your data."
        canonical="https://reklaim.io/auth"
      />
      
      {/* Lemonade-style Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-4">
          {/* Note: a mobile-menu icon used to render here as a no-op
           * placeholder <button>. Phase 6a R4 (REK-1541) removed it because
           * (a) it had no onClick wired up, (b) Copilot's a11y review flagged
           * the focusable-but-inert button as misleading for keyboard and
           * screen-reader users, and (c) the post-login dashboard owns the
           * actual mobile menu. If product specs a pre-login mobile menu
           * later, add a real `<Button>` here with the appropriate handler. */}
          <Link to="/">
            <img 
              src={reklaimWordmark} 
              alt="Reklaim" 
              className="h-6"
            />
          </Link>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => setMode('onboarding')}
            aria-label="Start over"
            title="Start over"
            className="h-auto w-auto p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-400 transition-colors"
          >
            <RotateCcw className="h-5 w-5" aria-hidden="true" />
          </Button>
          {/* Note: a HelpCircle icon used to render here as a no-op placeholder
           * <button>. Phase 6a R4 (REK-1541) removed it per Copilot's a11y
           * review -- focusable but inert is misleading. When pre-login help
           * is spec'd, add a real `<Button>` with a `useNavigate('/help')`
           * handler (or equivalent product decision). */}
        </div>
      </header>

      {/* One-tap login notice (e.g. expired link redirected from /auth/one-tap) */}
      {oneTapNotice && (
        <div className="px-6 py-4">
          <Alert variant="destructive" className="flex items-start gap-3 [&>svg]:static [&>svg]:translate-y-0 [&>svg~*]:pl-0">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <AlertTitle>{oneTapNotice}</AlertTitle>
              <AlertDescription>
                One-tap links are time-limited for security. Sign in with your
                email below and we&rsquo;ll get you back in.
              </AlertDescription>
            </div>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOneTapNotice(null)}
              aria-label="Dismiss notice"
              className="shrink-0 h-6 w-6 rounded-full p-0 leading-none text-destructive transition-colors hover:bg-destructive/20 hover:text-destructive"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </Button>
          </Alert>
        </div>
      )}

      {/* Account Locked Error */}
      {accountLockedError && (
        <div className="px-6 py-4">
          <Alert variant="destructive" className="flex items-start gap-3 [&>svg]:static [&>svg]:translate-y-0 [&>svg~*]:pl-0">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <AlertTitle>Account Locked</AlertTitle>
              <AlertDescription>
                Your account has been locked. Please contact{' '}
                <a href="mailto:support@reklaimyou.com" className="underline font-medium">
                  support@reklaimyou.com
                </a>{' '}
                for assistance.
              </AlertDescription>
            </div>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setAccountLockedError(null)}
              aria-label="Dismiss account-locked error"
              className="shrink-0 h-6 w-6 rounded-full p-0 leading-none text-destructive transition-colors hover:bg-destructive/20 hover:text-destructive"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </Button>
          </Alert>
        </div>
      )}

      {/* REK-1754: transient session-verify failure (fail-closed lock check).
          Distinct from the "Account Locked" banner above — this is a retryable
          state, so it must not mention support / imply a permanent lock. Hidden
          when the account is genuinely locked to avoid a double banner. */}
      {sessionError && !accountLockedError && (
        <div className="px-6 py-4">
          <Alert className="flex items-start gap-3 [&>svg]:static [&>svg]:translate-y-0 [&>svg~*]:pl-0">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <AlertTitle>Please sign in again</AlertTitle>
              <AlertDescription>{sessionError}</AlertDescription>
            </div>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setSessionError(null)}
              aria-label="Dismiss session error"
              className="shrink-0 h-6 w-6 rounded-full p-0 leading-none transition-colors hover:bg-muted"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </Button>
          </Alert>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col">
        {mode === 'onboarding' ? (
          <LemonadeOnboarding
            referralCode={refCode}
            initialEmail={signupEmail || initialEmail}
            sourcePage="auth"
            nextPath={nextPath}
            onSwitchToLogin={(email?: string, opts?: { alreadySent?: boolean }) => { setLoginEmail(email); setLoginAlreadySent(!!opts?.alreadySent); setSignupEmail(undefined); setMode('login'); }}
            onSignupStarted={handleSignupStarted}
            onSignupComplete={handleSignupComplete}
          />
        ) : (
          <LemonadeLogin
            initialEmail={loginInitialEmail}
            alreadySent={loginAlreadySent || sentFromUrl}
            nextPath={nextPath}
            onSwitchToSignup={(email?: string) => { const params = new URLSearchParams(); if (email) params.set('email', email); window.location.href = `/signup${params.toString() ? `?${params.toString()}` : ''}`; }}
            // REK-2008 (SSO.14): while the in-card sign-up pivot is active the
            // Clerk session activates mid-verify — suspend this page's
            // authenticated-redirect (above) so the pivot's own /onboarding
            // navigation wins, exactly like LemonadeOnboarding's signup flow.
            onPivotStateChange={setIsCompletingSignup}
          />
        )}
      </main>
    </div>
  );
};

export default Auth;
